import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import type { CanActivateFn } from '@angular/router';
import { ActivatedRoute, Router } from '@angular/router';
import type { School, User } from 'app/models';
import { AuthStateService } from 'app/auth/services/auth-state.service';
import { KioskModeService } from 'app/services/kiosk-mode.service';
import { UserInitService } from 'app/services/user-init.service';
import { UserService } from 'app/services/user.service';
import { combineLatest, of, race } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { redirectOnLoginProblem } from 'app/auth/guards/login-problem.guard';
import { HttpService } from '../services/http-service';

function adminFirstLoginShouldRedirect(user: User, school: School): boolean {
	return school.onboarding_status !== 'Launched' && user.isAdmin() && user.isFirstLogin();
}

export const rootRedirectGuard: CanActivateFn = () => {
	const router = inject(Router);
	const authStateService = inject(AuthStateService);
	const userService = inject(UserService);
	const activeRoute = inject(ActivatedRoute);
	const httpService = inject(HttpService);
	const userInitService = inject(UserInitService);
	const kioskModeService = inject(KioskModeService);
	const loginProblem$ = redirectOnLoginProblem();

	return combineLatest({
		isAuthenticated: toObservable(authStateService.isAuthenticated),
		settled: toObservable(authStateService.isAuthSettled),
	}).pipe(
		filter(({ settled }) => settled),
		switchMap(({ isAuthenticated }) => {
			if (!isAuthenticated) {
				return of(router.parseUrl('/'));
			}

			// The user only turns up once Init succeeds; if it can't, explain why instead of waiting.
			const destination$ = combineLatest([httpService.currentSchool$, userService.user$]).pipe(
				filter((pair): pair is [School | null, User] => pair[1] !== null),
				switchMap(([school, user]) => {
					if (user.isParent()) {
						return of(router.parseUrl('/parent'));
					}

					const kioskLocation = userInitService.dedicatedKioskLocation();
					if (kioskLocation) {
						kioskModeService.setTempRoom(kioskLocation);
						return of(router.parseUrl('/main/kioskMode/settings'));
					}

					return activeRoute.queryParams.pipe(
						map((params) => {
							const state = params.state;

							if (state) {
								const decodedState = decodeURIComponent(state);
								return router.parseUrl(`/${decodedState}`);
							}

							if (school && adminFirstLoginShouldRedirect(user, school)) {
								return router.parseUrl('/main/admin/get-started');
							}

							return router.parseUrl('/main');
						})
					);
				})
			);
			return race(destination$, loginProblem$).pipe(take(1));
		})
	);
};
